/**
 * @fileoverview Componente Molecule para mostrar y alternar respuestas rápidas de Mia en el módulo Inbox.
 * Permite gestionar la visualización y selección de respuestas sugeridas por Mia.
 *
 * @module ToggleMiaResponsesMC
 * @category Component - Molecule
 * @since 2.0.0
 * @version 3.1.0
 */
import React, { memo } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ToggleMiaPopoverMC from './ToggleMiaPopoverMC';
import { toggleMiaResponses } from '../../../../actions/inbox/conversationActions';
import { toSidebarPlatformKey } from '../../../../services/inbox/inboxMsChatPlatform';
import useSelectedConversation from '../../../hooks/shared/useSelectedConversation';
import useChatAvailability from '../../../hooks/inbox/useChatAvailability';

/**
 * @typedef {Object} ToggleMiaResponsesMCProps
 * @description Propiedades para el componente ToggleMiaResponsesMC.
 * @property {Array<{id: string, text: string}>} responses - Lista de respuestas sugeridas por Mia.
 * @property {function} onSelect - Función para manejar la selección de una respuesta.
 * @property {boolean} isOpen - Indica si el panel de respuestas está abierto.
 * @property {ReactNode} [children] - Elementos hijos adicionales.
 */
const ToggleMiaResponsesMC = memo(() => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const csrfToken = document.querySelector('[name=csrf-token]').content;

  const conversation = useSelectedConversation();
  const { isMiaPlatformConfigured } = useChatAvailability({ conversation });
  const normalizedPlatform = toSidebarPlatformKey(conversation?.platform)?.toLowerCase();
  const showToggleMia = isMiaPlatformConfigured && !!conversation?.id;

  const popoverDismissed = sessionStorage.getItem(`mia_popover_dismissed_${normalizedPlatform}_${conversation?.id}`);

  const setPopoverDismissed = (dismissed) => {
    if (conversation?.hasMia) {
      sessionStorage.setItem(`mia_popover_dismissed_${normalizedPlatform}_${conversation?.id}`, dismissed);
    }
  };

  const toggleMiaResponse = () => {
    const wasActive = conversation?.hasMia;
    if (!wasActive) {
      setPopoverDismissed(true);
    }
    dispatch(toggleMiaResponses(
      conversation?.id,
      csrfToken,
      { platform: conversation?.platform },
      wasActive
    ))
      .then((result) => {
        if (!result?.success) {
          showtoast(result?.error || t('chatbot_ai.general.save_error'));
          return;
        }
        showtoast(
          result.hasMia
            ? t('chatbot_ai.general.activate_chatbot')
            : t('chatbot_ai.general.deactivate_chatbot')
        );
      })
      .catch((error) => {
        showtoast(error.message || t('chatbot_ai.general.save_error'));
      });
  };

  if (!showToggleMia) return null;

  return (
    <>
      {conversation?.hasMia && !popoverDismissed && (
        <ToggleMiaPopoverMC
          tooltipId="miaPopover"
          onDeactivate={toggleMiaResponse}
          onClose={() => setPopoverDismissed(true)}
        />
      )}

      <div className="tw-flex tw-items-center tw-gap-2 tw-h-8">
        <div
          id="miaPopover"
          onClick={toggleMiaResponse}
          className="tw-flex tw-items-center tw-cursor-pointer"
        >
          <div
            className={`tw-relative tw-w-8 tw-h-4 tw-rounded-full tw-transition-colors ${conversation?.hasMia ? 'tw-bg-[#7BD6A2]' : 'tw-bg-gray-200'
            }`}
            style={{
              backgroundColor: conversation?.hasMia ? '#7BD6A2' : '#E4E4E4'
            }}
          >
            <div
              className={`tw-absolute tw-top-1/2 tw-w-2 tw-h-2 tw-bg-white tw-rounded-full tw-transform -tw-translate-y-1/2 tw-transition-transform tw-shadow-sm ${conversation?.hasMia ? 'tw-translate-x-5' : 'tw-translate-x-1'}`}
            />
          </div>
        </div>

        <span className="tw-text-xs tw-text-gray-400 tw-flex tw-items-center">
          {t('chatbot_ai.general.mia_label_title')}
        </span>
      </div>
    </>
  );
});

export default ToggleMiaResponsesMC;
